import { QueryClient, type QueryKey } from '@tanstack/react-query'
import { trainingQueryKeys } from '../config/query-keys'
import { Training } from '../types/base'

interface InfiniteTrainingListData {
	pages: Training[][]
	pageParams: unknown[]
}

const isInfiniteTrainingListData = (
	data: unknown
): data is InfiniteTrainingListData => {
	return Boolean(
		data &&
			typeof data === 'object' &&
			Array.isArray((data as InfiniteTrainingListData).pages)
	)
}

const removeTrainingFromList = (
	trainings: Training[],
	workoutId: number
): Training[] => {
	return trainings.filter(training => training.id_workout !== workoutId)
}

const upsertTrainingInList = (
	trainings: Training[],
	training: Training
): Training[] => {
	return [training, ...removeTrainingFromList(trainings, training.id_workout)]
}

const shouldPatchTrainingListCache = (queryKey: QueryKey): boolean => {
	if (
		queryKey[0] !== trainingQueryKeys.all[0] ||
		queryKey[1] !== trainingQueryKeys.lists()[1]
	) {
		return false
	}

	const listDescriptor = queryKey[2]

	if (listDescriptor === 'home-preview') {
		return true
	}

	if (typeof listDescriptor !== 'object' || listDescriptor === null) {
		return true
	}

	const search = (listDescriptor as { search?: unknown }).search
	return typeof search !== 'string' || search.trim().length === 0
}

const patchTrainingListCaches = (
	queryClient: QueryClient,
	updater: (cachedData: unknown) => unknown
): void => {
	queryClient
		.getQueryCache()
		.findAll({ queryKey: trainingQueryKeys.lists() })
		.forEach(query => {
			if (shouldPatchTrainingListCache(query.queryKey)) {
				queryClient.setQueryData(query.queryKey, updater)
			}
		})
}

export const invalidateTrainingLists = (
	queryClient: QueryClient
): Promise<void> => {
	return queryClient.invalidateQueries({
		queryKey: trainingQueryKeys.lists(),
	})
}

export const scheduleTrainingListsInvalidation = (
	queryClient: QueryClient
): void => {
	void invalidateTrainingLists(queryClient)
}

export const upsertTrainingInCachedLists = (
	queryClient: QueryClient,
	training: Training
): void => {
	patchTrainingListCaches(queryClient, cachedData => {
		if (Array.isArray(cachedData)) {
			return upsertTrainingInList(cachedData, training)
		}

		if (isInfiniteTrainingListData(cachedData)) {
			const pagesWithoutTraining = cachedData.pages.map(page =>
				removeTrainingFromList(page, training.id_workout)
			)
			const [firstPage = [], ...otherPages] = pagesWithoutTraining

			return {
				...cachedData,
				pages: [upsertTrainingInList(firstPage, training), ...otherPages],
			}
		}

		return cachedData
	})
}

export const removeTrainingFromCachedLists = (
	queryClient: QueryClient,
	workoutId: number
): void => {
	patchTrainingListCaches(queryClient, cachedData => {
		if (Array.isArray(cachedData)) {
			return removeTrainingFromList(cachedData, workoutId)
		}

		if (isInfiniteTrainingListData(cachedData)) {
			return {
				...cachedData,
				pages: cachedData.pages.map(page =>
					removeTrainingFromList(page, workoutId)
				),
			}
		}

		return cachedData
	})
}
